<template>
  <ElementLayout>
    <template #element>
      <div class="audio-recorder-element">
        <!-- === State 1: Recorded Audio Present === -->
        <div v-if="hasAudio" class="mb-2">
          <p>
            <i class="ri-mic-line"></i> Recording present
            <!-- Check props.modelValue for size, as it's the canonical data -->
            <span v-if="props.modelValue instanceof Blob && props.modelValue.size"> ({{ formatBytes(props.modelValue.size) }})</span>
            <!-- Fallback text if size isn't immediately available -->
            <span v-else-if="audioUrl"> (Preview available)</span>
            <BButton size="sm" variant="outline-danger" @click="clearRecording" class="ms-2" title="Remove Recording">
              <i class="ri-close-line"></i>
            </BButton>
          </p>
          <!-- audioUrl is now set directly in onstop AND by the watcher -->
          <audio v-if="audioUrl" :src="audioUrl" controls class="w-100"></audio>
          <p v-else class="text-danger small">Generating audio preview...</p>
        </div>

        <!-- === State 2: No Audio / Ready to Record === -->
        <div v-else>
          <BButton @click="startRecording" :disabled="isRecording" variant="primary" class="me-2">
            <i class="ri-record-circle-line"></i> {{ isRecording ? 'Recording...' : 'Start Recording' }}
          </BButton>
          <BButton @click="stopRecording" :disabled="!isRecording" variant="secondary">
            <i class="ri-stop-fill"></i> Stop
          </BButton>
          <p v-if="statusMessage" class="mt-2 small text-muted">{{ statusMessage }}</p>
        </div>
      </div>
    </template>
  </ElementLayout>
</template>

<script>
import { defineElement } from '@vueform/vueform';
import { ref, watch, onMounted, onBeforeUnmount } from 'vue';

export default defineElement({
  name: 'AudioRecorderElement',
  props: {
    modelValue: {
      type: [Blob, File, null],
      default: null,
    },
  },
  setup(props, { element }) {
    const isRecording = ref(false);
    const mediaRecorder = ref(null);
    const audioChunks = ref([]);
    const statusMessage = ref('');
    const audioUrl = ref(null);
    const hasAudio = ref(false);
    const currentBlob = ref(null); // Keep track of the blob used for the current URL

    const updateAudioUrl = (blob) => {
      //console.log('[updateAudioUrl] Called with:', blob);

      // Avoid unnecessary work if the blob hasn't actually changed
      // This prevents issues if the watcher runs after we manually set it
      if (blob === currentBlob.value && audioUrl.value) {
          //console.log('[updateAudioUrl] Blob is the same as current and URL exists, skipping update.');
          return;
      }

      // Revoke previous URL if it exists
      if (audioUrl.value) {
        //console.log('[updateAudioUrl] Revoking previous URL:', audioUrl.value);
        URL.revokeObjectURL(audioUrl.value);
        audioUrl.value = null;
      }

      // Create new URL if blob is valid
      if (blob instanceof Blob && blob.size > 0) {
        try {
          audioUrl.value = URL.createObjectURL(blob);
          currentBlob.value = blob; // Store the blob associated with the URL
          //console.log('[updateAudioUrl] Created new URL:', audioUrl.value);
        } catch (error) {
          console.error("[updateAudioUrl] Error creating Object URL:", error);
          audioUrl.value = null;
          currentBlob.value = null;
        }
      } else {
         //console.log('[updateAudioUrl] Blob is null or invalid, URL remains null.');
         currentBlob.value = null; // Ensure currentBlob is also null
         audioUrl.value = null; // Ensure audioUrl is null if blob is invalid
      }
    };

    const startRecording = async () => {
      //console.log('[startRecording] Initiated.');
      // Clear previous state *before* starting
      clearRecording(true); // Pass flag to indicate it's part of starting a new recording
      statusMessage.value = ''; // Reset status after clear

      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        // Try common mime types, fallback to default
        const mimeTypes = ['audio/webm;codecs=opus', 'audio/ogg;codecs=opus', 'audio/webm', 'audio/ogg'];
        let supportedMimeType = '';
        for (const mimeType of mimeTypes) {
            if (MediaRecorder.isTypeSupported(mimeType)) {
                supportedMimeType = mimeType;
                break;
            }
        }
        //console.log(`[startRecording] Using mimeType: ${supportedMimeType || 'default'}`);

        const recorder = new MediaRecorder(stream, { mimeType: supportedMimeType || undefined });
        mediaRecorder.value = recorder;

        recorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            //console.log(`[recorder.ondataavailable] Received chunk, size: ${event.data.size}`);
            audioChunks.value.push(event.data);
          }
        };

        recorder.onstop = () => {
          //console.log('[recorder.onstop] Recording stopped.');
          isRecording.value = false; // Set recording state false *before* processing

          if (audioChunks.value.length === 0) {
              console.warn('[recorder.onstop] No audio chunks recorded.');
              statusMessage.value = 'Recording stopped, but no audio data received.';
              hasAudio.value = false; // Stay in recording state display
              // Stop tracks if they weren't stopped already
              stream?.getTracks().forEach(track => {
                  if (track.readyState === 'live') track.stop();
              });
              return; // Exit early
          }

          const audioBlob = new Blob(audioChunks.value, { type: recorder.mimeType || 'audio/webm' });
          audioChunks.value = []; // Clear chunks immediately after creating blob

          //console.log('[recorder.onstop] Created blob:', audioBlob);

          // --- KEY CHANGE: Update URL and UI state directly ---
          updateAudioUrl(audioBlob); // <<-- Create the Object URL immediately
          hasAudio.value = true;     // <<-- Show the player section
          statusMessage.value = 'Recording finished.';

          // --- Update Vueform model value (watcher will eventually sync if needed) ---
          //console.log('[recorder.onstop] Calling element.update with new blob.');
          element.update(audioBlob);

          // Stop media stream tracks
          stream.getTracks().forEach(track => {
              if (track.readyState === 'live') track.stop();
          });
          //console.log('[recorder.onstop] Media stream tracks stopped.');
        };

        recorder.onerror = (event) => {
          console.error('MediaRecorder Error:', event.error);
          statusMessage.value = `Recording error: ${event.error.name || 'Unknown error'}`;
          isRecording.value = false;
          hasAudio.value = false;
          updateAudioUrl(null); // Clear URL on error
          audioChunks.value = []; // Clear any partial chunks
          stream?.getTracks().forEach(track => {
              if (track.readyState === 'live') track.stop();
          });
        };

        // Start recording
        recorder.start();
        isRecording.value = true;
        statusMessage.value = 'Recording...';
        //console.log('[startRecording] Recording started.');

      } catch (err) {
        console.error('Error starting recording:', err);
        statusMessage.value = `Could not start recording: ${err.message || 'Permission denied?'}`;
        isRecording.value = false;
        hasAudio.value = false;
        // Clean up just in case
        mediaRecorder.value = null;
        audioChunks.value = [];
      }
    };

    const stopRecording = () => {
      if (mediaRecorder.value && isRecording.value) {
        //console.log('[stopRecording] Attempting to stop MediaRecorder.');
        // Check state before stopping, sometimes 'inactive' can cause issues if called twice
        if (mediaRecorder.value.state === 'recording') {
            try {
                mediaRecorder.value.stop(); // onstop handler will do the rest
                //console.log('[stopRecording] MediaRecorder.stop() called.');
            } catch (e) {
                console.error('[stopRecording] Error calling MediaRecorder.stop():', e);
                // Manual cleanup if stop fails?
                isRecording.value = false;
                statusMessage.value = 'Error stopping recording.';
                mediaRecorder.value?.stream?.getTracks().forEach(track => {
                   if (track.readyState === 'live') track.stop();
                });
            }
        } else {
             console.warn('[stopRecording] MediaRecorder state is not "recording", current state:', mediaRecorder.value.state);
             // If already stopped or inactive, maybe manually trigger cleanup?
             isRecording.value = false; // Force state update if needed
             // Ensure tracks are stopped if recorder is in a weird state
             mediaRecorder.value?.stream?.getTracks().forEach(track => {
                if (track.readyState === 'live') track.stop();
             });
        }
      } else {
         console.warn('[stopRecording] Stop called but no active recorder or not recording.');
         isRecording.value = false; // Ensure state is correct
      }
    };

    // Modified clearRecording to accept an optional flag
    const clearRecording = (calledFromStart = false) => {
      //console.log('[clearRecording] Called.');
      if (isRecording.value && mediaRecorder.value) {
        //console.log('[clearRecording] Stopping active recording before clearing.');
        // Prevent the normal onstop logic ONLY if we are clearing manually (not during start)
        if (!calledFromStart) {
            mediaRecorder.value.onstop = null;
            mediaRecorder.value.ondataavailable = null;
            mediaRecorder.value.onerror = null;
        }
        // Stop tracks immediately
        mediaRecorder.value?.stream?.getTracks().forEach(track => {
            if (track.readyState === 'live') track.stop();
        });
        // Try stopping the recorder if it's recording
        if (mediaRecorder.value.state === 'recording') {
            try {
                mediaRecorder.value.stop();
            } catch (e) {
                console.warn('[clearRecording] Error stopping recorder during clear:', e);
            }
        }
      }

      // Clear Vueform model value only if not called during start (to avoid race conditions)
      if (!calledFromStart) {
          element.update(null);
      }

      // Reset internal state
      updateAudioUrl(null); // Explicitly clear the URL and revoke if necessary
      hasAudio.value = false;
      isRecording.value = false;
      if (!calledFromStart) { // Don't overwrite "Recording..." status if called from start
          statusMessage.value = 'Recording cleared.';
      }
      audioChunks.value = [];
      mediaRecorder.value = null; // Release recorder reference
      currentBlob.value = null;
      //console.log('[clearRecording] State cleared.');
    };


    const formatBytes = (bytes, decimals = 2) => {
        if (typeof bytes !== 'number' || !bytes || bytes === 0) return '0 Bytes';
        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        const size = (i >= 0 && i < sizes.length) ? parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) : 0;
        return size + ' ' + (sizes[i] || sizes[0]);
     };

    // Watcher: Primarily for initial load and external changes
    watch(() => props.modelValue, (newValue, oldValue) => {
      //console.log('[Watcher] modelValue changed.', { newValue, oldValue });

      // Sync audio URL with the definitive modelValue
      // This handles loading existing data or if element.update eventually triggers it
      updateAudioUrl(newValue);

      // Sync local UI state with the prop value
      hasAudio.value = newValue instanceof Blob;

      // Update status only if not actively recording or just finished
      if (!isRecording.value && statusMessage.value !== 'Recording finished.') {
          if (newValue && !oldValue) {
              statusMessage.value = 'Recording present.';
          } else if (!newValue && oldValue && statusMessage.value !== 'Recording cleared.') {
              statusMessage.value = 'Recording removed.';
          }
      }

    }, { immediate: true });

    onMounted(() => {
      //console.log('[onMounted] Component mounted. Initial modelValue:', props.modelValue);
      // Initial state (URL, hasAudio) is set by the immediate watcher
    });

    onBeforeUnmount(() => {
        //console.log('[onBeforeUnmount] Unmounting, cleaning up...');
        clearRecording(true); // Use clearRecording logic for cleanup
     });

    return {
      props,
      isRecording,
      statusMessage,
      audioUrl,
      hasAudio,
      startRecording,
      stopRecording,
      clearRecording: () => clearRecording(false), // Ensure public call doesn't use the flag
      formatBytes,
      Blob
    };
  }
})
</script>

<style scoped>
.audio-recorder-element { padding: 10px; border: 1px solid #ccc; border-radius: 4px; }
audio { width: 100%; margin-top: 5px; }
.btn-outline-danger { padding: 0.1rem 0.4rem; font-size: 0.9rem; line-height: 1; }
</style>
